/**
 * Rysuje siatkę cm, znaczniki mm i margines na canvasie tekstury.
 *
 * Układ współrzędnych canvasa:
 *   canvas_x = (xMm + widthMm/2) / widthMm * canvasW
 *   canvas_y = (heightMm/2 - yMm) / heightMm * canvasH   (Y odwrócone)
 * — zgodnie z UV mappingiem w Viewport3D / Three.js flipY=true.
 */

export interface GridOverlaySettings {
  showGridCm:  boolean;
  showGridMm:  boolean;
  showMargin:  boolean;
  negGMColor:  boolean;
  marginMm:    number;
  widthMm:     number;
  heightMm:    number;
}

export function drawGridOverlay(
  ctx:     CanvasRenderingContext2D,
  canvasW: number,
  canvasH: number,
  s:       GridOverlaySettings,
): void {
  if (!s.showGridCm && !s.showGridMm && !s.showMargin) return;

  const scaleX = canvasW / s.widthMm;   // px / mm
  const scaleY = canvasH / s.heightMm;

  // mm → canvas px (Y odwrócone – kompensacja flipY=true w Three.js)
  const cx = (xMm: number) => (xMm + s.widthMm  / 2) * scaleX;
  const cy = (yMm: number) => (s.heightMm / 2 - yMm) * scaleY;

  const gridColor   = s.negGMColor ? '#00cccc' : '#ff3333';
  const marginColor = s.negGMColor ? '#ff3333' : '#00cccc';

  const halfW = s.widthMm  / 2;
  const halfH = s.heightMm / 2;

  // ── Siatka cm ──────────────────────────────────────────────────────────────
  if (s.showGridCm || s.showGridMm) {

    // Pierwsze wielokrotności 10 mm wchodzące w zakres materiału
    const x0 = Math.ceil(-halfW / 10) * 10;
    const y0 = Math.ceil(-halfH / 10) * 10;

    if (s.showGridCm) {
      ctx.save();

      // Pionowe linie (x = const)
      for (let xMm = x0; xMm <= halfW + 0.01; xMm += 10) {
        const isBold = Math.round(Math.abs(xMm)) % 50 === 0;
        ctx.strokeStyle = gridColor;
        ctx.lineWidth   = isBold ? 2 : 1;
        ctx.globalAlpha = isBold ? 0.80 : 0.50;
        ctx.beginPath();
        ctx.moveTo(cx(xMm), 0);
        ctx.lineTo(cx(xMm), canvasH);
        ctx.stroke();
      }

      // Poziome linie (y = const)
      for (let yMm = y0; yMm <= halfH + 0.01; yMm += 10) {
        const isBold = Math.round(Math.abs(yMm)) % 50 === 0;
        ctx.strokeStyle = gridColor;
        ctx.lineWidth   = isBold ? 2 : 1;
        ctx.globalAlpha = isBold ? 0.80 : 0.50;
        ctx.beginPath();
        ctx.moveTo(0, cy(yMm));
        ctx.lineTo(canvasW, cy(yMm));
        ctx.stroke();
      }

      ctx.restore();
    }

    // ── Znaczniki mm wzdłuż linii cm ────────────────────────────────────────
    if (s.showGridMm) {
      // Długość znacznika = 2 mm (po 1 mm po obu stronach linii)
      const tickHalfX = 1 * scaleX;
      const tickHalfY = 1 * scaleY;

      ctx.save();
      ctx.strokeStyle = gridColor;
      ctx.globalAlpha = 0.55;
      ctx.lineWidth   = 1;

      // Znaczniki wzdłuż pionowych linii cm (poziome kreski co 1 mm)
      for (let xMm = x0; xMm <= halfW + 0.01; xMm += 10) {
        const px = cx(xMm);
        for (let yMm = -halfH; yMm <= halfH; yMm += 1) {
          if (Math.round(Math.abs(yMm)) % 10 === 0) continue; // pomiń przy liniach cm
          const py = cy(yMm);
          ctx.beginPath();
          ctx.moveTo(px - tickHalfX, py);
          ctx.lineTo(px + tickHalfX, py);
          ctx.stroke();
        }
      }

      // Znaczniki wzdłuż poziomych linii cm (pionowe kreski co 1 mm)
      for (let yMm = y0; yMm <= halfH + 0.01; yMm += 10) {
        const py = cy(yMm);
        for (let xMm = -halfW; xMm <= halfW; xMm += 1) {
          if (Math.round(Math.abs(xMm)) % 10 === 0) continue;
          const px = cx(xMm);
          ctx.beginPath();
          ctx.moveTo(px, py - tickHalfY);
          ctx.lineTo(px, py + tickHalfY);
          ctx.stroke();
        }
      }

      ctx.restore();
    }
  }

  // ── Margines ───────────────────────────────────────────────────────────────
  if (s.showMargin && s.marginMm > 0) {
    const mx1 = cx(-halfW + s.marginMm);
    const my1 = cy( halfH - s.marginMm);
    const mx2 = cx( halfW - s.marginMm);
    const my2 = cy(-halfH + s.marginMm);
    ctx.save();
    ctx.strokeStyle = marginColor;
    ctx.lineWidth   = 2;
    ctx.globalAlpha = 0.90;
    ctx.strokeRect(mx1, my1, mx2 - mx1, my2 - my1);
    ctx.restore();
  }
}
